import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, useInView } from 'framer-motion';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Plus, 
  Search, 
  Users, 
  FileText, 
  Calendar,
  MoreVertical,
  Eye,
  Edit,
  FolderOpen,
  ArrowUpDown,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

function useIsMobile() {
  const [m, setM] = useState(false);
  useEffect(() => { const c = () => setM(window.innerWidth < 768); c(); window.addEventListener('resize', c); return () => window.removeEventListener('resize', c); }, []);
  return m;
}

function AnimatedNumber({ value, duration = 1 }) {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  useEffect(() => {
    if (isInView && value > 0) {
      let start = 0;
      const end = parseInt(value);
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) { setDisplayValue(end); clearInterval(timer); }
        else setDisplayValue(Math.floor(start));
      }, 1000 / 60);
      return () => clearInterval(timer);
    } else if (value === 0) setDisplayValue(0);
  }, [isInView, value, duration]);
  return <span ref={ref}>{displayValue}</span>;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CLIENTS_PER_PAGE = 12;

export default function ClientDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', age: '' });
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null);
  const [sortBy, setSortBy] = useState('last_updated'); // 'name', 'last_updated', 'plans_created'
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchClients();
    fetchUsage();
  }, []);

  useEffect(() => {
    let result = clients;
    
    // Filter by search query
    if (searchQuery) {
      result = result.filter(client =>
        client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Sort
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'plans_created':
          return (b.plans_count || 0) - (a.plans_count || 0);
        case 'last_updated':
        default:
          return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
      }
    });
    
    setFilteredClients(result);
    setCurrentPage(1); // Reset to page 1 when filters change
  }, [searchQuery, clients, sortBy]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredClients.length / CLIENTS_PER_PAGE);
  const startIndex = (currentPage - 1) * CLIENTS_PER_PAGE;
  const endIndex = startIndex + CLIENTS_PER_PAGE;
  const currentClients = filteredClients.slice(startIndex, endIndex);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/advisor/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/advisor/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(response.data);
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const handleViewLatestPlan = async (client) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/advisor/professional-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const plans = response.data.plans || [];
      const clientPlans = plans.filter(plan => 
        plan.client_name === client.name || plan.client_email === client.email
      );
      
      if (clientPlans.length > 0) {
        const mostRecentPlan = clientPlans.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        )[0];
        navigate(`/advisor/plan/${mostRecentPlan.plan_id}`);
      } else {
        const createPlan = window.confirm(`No plans found for ${client.name}. Create one now?`);
        if (createPlan) {
          navigate(`/advisor/create-plan?clientName=${encodeURIComponent(client.name)}&clientEmail=${encodeURIComponent(client.email)}`);
        }
      }
    } catch (error) {
      console.error('Error fetching client plans:', error);
      alert('Error loading client plans. Please try again.');
    }
  };

  const handleViewAllPlans = (client) => {
    // Navigate to My Plans page with client filter
    navigate(`/advisor/plans?email=${encodeURIComponent(client.email)}`);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setEditForm({
      name: client.name || '',
      email: client.email || '',
      age: client.age || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.name || !editForm.email) {
      alert('Name and email are required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/advisor/clients/${editingClient.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Client updated successfully!');
      setEditDialogOpen(false);
      fetchClients();
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Error updating client. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'pending': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      'completed': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
    };
    return colors[status] || colors.active;
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8">
        <Navigation user={user} onLogout={onLogout} />
        <div data-testid="clients-skeleton" className="max-w-7xl mx-auto animate-pulse">
          <div className="h-10 w-64 bg-gray-800 rounded-lg mb-2" />
          <div className="h-5 w-48 bg-gray-800/60 rounded mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-gray-800/40 rounded-xl" />)}
          </div>
          <div className="h-14 bg-gray-800/40 rounded-xl mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => <div key={i} className="h-48 bg-gray-800/40 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="client-dashboard" className="min-h-screen p-4 lg:p-8 relative overflow-hidden">
      <Navigation user={user} onLogout={onLogout} />
      {!isMobile && (
        <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
          <motion.div className="absolute w-[400px] h-[400px] rounded-full bg-gradient-to-r from-blue-600/[0.06] to-indigo-500/[0.06] blur-3xl" animate={{ x: [0, 30, 0], y: [0, -20, 0] }} transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }} style={{ top: '-5%', right: '10%' }} />
        </div>
      )}
      <motion.div className="max-w-7xl mx-auto relative z-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
        
        {/* Header */}
        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <motion.div className="w-2 h-8 rounded-full bg-gradient-to-b from-blue-500 to-indigo-400" initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 0.4, delay: 0.2 }} />
                <h1 data-testid="clients-header" className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">
                  <span className="bg-gradient-to-r from-blue-400 to-indigo-300 bg-clip-text text-transparent">Client</span> Dashboard
                </h1>
              </div>
              <p className="text-gray-600 dark:text-gray-400 ml-5 text-sm">Manage your clients and their financial plans</p>
            </div>
            <div className="flex-shrink-0 w-full sm:w-auto">
              <Button
                data-testid="new-client-plan-btn"
                onClick={() => {
                  if (usage?.subscription_status === 'expired') {
                    navigate('/advisor/pricing');
                  } else {
                    navigate('/advisor/create-plan');
                  }
                }}
                disabled={usage?.subscription_status === 'expired'}
                className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-600/20"
                title={usage?.subscription_status === 'expired' ? 'Trial expired - Choose a plan to continue' : ''}
              >
                <Plus className="w-4 h-4 mr-2" />
                {usage?.subscription_status === 'expired' ? 'Trial Expired - Choose Plan' : 'New Client Plan'}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div data-testid="clients-stats" className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {[
            { label: 'Total Clients', value: clients.length, sub: 'All-time', icon: Users, gradient: 'from-blue-500 to-cyan-400', iconBg: 'bg-blue-500/10', iconColor: 'text-blue-400' },
            { label: 'Plans Created', value: clients.reduce((sum, c) => sum + (c.plans_count || 0), 0), sub: 'All-time', icon: FileText, gradient: 'from-emerald-500 to-teal-400', iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-400' },
            { label: 'New Clients', value: clients.filter(c => { const d = new Date(c.created_at); const n = new Date(); return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear(); }).length, sub: 'This month', icon: Calendar, gradient: 'from-purple-500 to-violet-400', iconBg: 'bg-purple-500/10', iconColor: 'text-purple-400' },
          ].map((stat, idx) => (
            <motion.div
              key={stat.label}
              data-testid={`clients-stat-${idx}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: isMobile ? 0 : 0.1 + idx * 0.07 }}
              whileHover={!isMobile ? { y: -4, transition: { duration: 0.25 } } : {}}
              className="group"
            >
              <Card className="p-5 border-gray-200 dark:border-gray-800/60 hover:border-gray-300 dark:hover:border-gray-700/80 transition-all duration-300 hover:shadow-xl hover:shadow-black/20 relative overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-[0.04] transition-opacity duration-500`} />
                <div className="relative z-10 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium tracking-wide uppercase text-gray-500 mb-1">{stat.label}</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-gray-100"><AnimatedNumber value={stat.value} /></p>
                    <p className="text-xs text-gray-500 mt-1">{stat.sub}</p>
                  </div>
                  <motion.div className={`w-10 h-10 rounded-lg ${stat.iconBg} flex items-center justify-center`} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2 + idx * 0.07, type: "spring", stiffness: 260 }}>
                    <stat.icon className={`w-5 h-5 ${stat.iconColor}`} />
                  </motion.div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Search and Sort Bar */}
        <Card className="glass p-4 mb-6 dark:bg-gray-800">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 dark:text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search clients by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  {sortBy === 'name' ? 'Name' : sortBy === 'plans_created' ? 'Plans Created' : 'Last Updated'}
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSortBy('last_updated')}>
                  Last Updated
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('name')}>
                  Name
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('plans_created')}>
                  Plans Created
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </Card>

        {/* Clients List */}
        {filteredClients.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <Card data-testid="clients-empty-state" className="p-12 border-gray-200 dark:border-gray-800/60 text-center">
              <motion.div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 flex items-center justify-center" animate={!isMobile ? { y: [0, -6, 0] } : {}} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}>
                <Sparkles className="w-7 h-7 text-blue-400/60" />
              </motion.div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {clients.length === 0 ? 'No clients yet' : 'No clients found'}
              </h3>
              <p className="text-gray-500 text-sm mb-6">
                {clients.length === 0 
                  ? 'Create your first professional financial plan for a client' 
                  : 'Try adjusting your search query'}
              </p>
            {clients.length === 0 && (
              <Button 
                onClick={() => {
                  if (usage?.subscription_status === 'expired') {
                    navigate('/advisor/pricing');
                  } else {
                    navigate('/advisor/create-plan');
                  }
                }}
                disabled={usage?.subscription_status === 'expired'}
                className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                title={usage?.subscription_status === 'expired' ? 'Trial expired - Choose a plan to continue' : ''}
              >
                <Plus className="w-4 h-4 mr-2" />
                {usage?.subscription_status === 'expired' ? 'Trial Expired - Choose Plan' : 'Create First Plan'}
              </Button>
            )}
          </Card>
          </motion.div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {currentClients.map((client, index) => (
                <motion.div
                  key={client.id || client.email || index}
                  data-testid={`client-card-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: isMobile ? 0 : 0.1 + index * 0.05 }}
                  whileHover={!isMobile ? { y: -4, transition: { duration: 0.25 } } : {}}
                  className="group"
                >
                <Card className="p-5 border-gray-200 dark:border-gray-800/60 hover:border-gray-300 dark:hover:border-gray-700/80 transition-all duration-300 hover:shadow-xl hover:shadow-black/20">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
                        {client.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">{client.name}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{client.email}</p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewAllPlans(client)}>
                          <FolderOpen className="w-4 h-4 mr-2" />
                          View All Plans
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEditClient(client)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Client
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Age:</span>
                      <span className="font-medium text-gray-800 dark:text-gray-100">{client.age} years</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Plans Created:</span>
                      <span className="font-medium text-gray-800 dark:text-gray-100">{client.plans_count || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Last Updated:</span>
                      <span className="font-medium text-gray-800 dark:text-gray-100">
                        {new Date(client.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                    <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getStatusColor(client.status || 'active')}`}>
                      {(client.status || 'active').toUpperCase()}
                    </span>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/advisor/create-plan?clientEmail=${encodeURIComponent(client.email)}&client=${encodeURIComponent(client.name)}`)}
                        disabled={usage?.subscription_status === 'expired'}
                        title={usage?.subscription_status === 'expired' ? 'Trial expired' : 'Create new plan for this client'}
                        className="w-full sm:w-auto"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Create New Plan
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleViewLatestPlan(client)}
                        className="w-full sm:w-auto"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Latest
                      </Button>
                    </div>
                  </div>
                </Card>
                </motion.div>
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}

      </motion.div>

      {/* Edit Client Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
            <DialogDescription>
              Update client information. Changes will be saved immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={editForm.name}
                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                placeholder="Client name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                placeholder="client@example.com"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="age">Age</Label>
              <Input
                id="age"
                type="number"
                value={editForm.age}
                onChange={(e) => setEditForm({...editForm, age: e.target.value})}
                placeholder="35"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setEditDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEdit}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
